import React, { useState, useEffect, useRef } from 'react';
import { HashRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';

/* ═══════════════════════════════════════════
   GLOBAL STYLES
   ═══════════════════════════════════════════ */
const GlobalStyles = () => (
  <style>{`
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

    :root {
      --primary: #00f0ff;
      --primary-dim: #00a8b3;
      --secondary: #ff6b35;
      --accent: #a855f7;
      --bg-dark: #030712;
      --bg-card: rgba(15, 23, 42, 0.7);
      --bg-card-hover: rgba(15, 23, 42, 0.9);
      --text: #e2e8f0;
      --text-dim: #94a3b8;
      --border: rgba(0, 240, 255, 0.15);
      --glow: 0 0 30px rgba(0, 240, 255, 0.3);
    }

    html { scroll-behavior: smooth; }

    body {
      font-family: 'Rajdhani', sans-serif;
      background: var(--bg-dark);
      color: var(--text);
      overflow-x: hidden;
      line-height: 1.6;
    }

    a { text-decoration: none; color: inherit; }

    /* Scrollbar */
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: var(--bg-dark); }
    ::-webkit-scrollbar-thumb { background: var(--primary-dim); border-radius: 3px; }

    /* Animations */
    @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-20px)} }
    @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
    @keyframes slideUp { from{opacity:0;transform:translateY(60px)} to{opacity:1;transform:translateY(0)} }
    @keyframes slideIn { from{opacity:0;transform:translateX(-40px)} to{opacity:1;transform:translateX(0)} }
    @keyframes glow { 0%,100%{box-shadow:0 0 20px rgba(0,240,255,0.2)} 50%{box-shadow:0 0 40px rgba(0,240,255,0.5)} }
    @keyframes rotate { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    @keyframes fadeIn { from{opacity:0} to{opacity:1} }
    @keyframes scaleIn { from{opacity:0;transform:scale(0.8)} to{opacity:1;transform:scale(1)} }
    @keyframes borderGlow {
      0%,100%{border-color:rgba(0,240,255,0.2)}
      50%{border-color:rgba(0,240,255,0.6)}
    }
    @keyframes particleFloat {
      0%{transform:translate(0,0) scale(1);opacity:0.6}
      50%{transform:translate(var(--tx),var(--ty)) scale(1.5);opacity:1}
      100%{transform:translate(var(--tx2),var(--ty2)) scale(0.5);opacity:0}
    }
    @keyframes countUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
    @keyframes hexSpin { 0%{transform:rotate(0deg) scale(1)} 50%{transform:rotate(180deg) scale(1.1)} 100%{transform:rotate(360deg) scale(1)} }

    .animate-on-scroll {
      opacity: 0;
      transform: translateY(40px);
      transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .animate-on-scroll.visible {
      opacity: 1;
      transform: translateY(0);
    }
  `}</style>
);

/* ═══════════════════════════════════════════
   SCROLL ANIMATION HOOK
   ═══════════════════════════════════════════ */
function useScrollAnimation() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

/* ═══════════════════════════════════════════
   PARTICLE BACKGROUND
   ═══════════════════════════════════════════ */
const ParticleBackground = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    let particles = [];
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.5;
        this.speedY = (Math.random() - 0.5) * 0.5;
        this.opacity = Math.random() * 0.5 + 0.1;
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width || this.y < 0 || this.y > canvas.height) this.reset();
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 240, 255, ${this.opacity})`;
        ctx.fill();
      }
    }

    for (let i = 0; i < 80; i++) particles.push(new Particle());

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => { p.update(); p.draw(); });
      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 240, 255, ${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', zIndex:0, pointerEvents:'none' }} />;
};

/* ═══════════════════════════════════════════
   HEXAGON LOGO SVG
   ═══════════════════════════════════════════ */
const HexLogo = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100">
    <defs>
      <linearGradient id="hexGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00f0ff" />
        <stop offset="100%" stopColor="#a855f7" />
      </linearGradient>
    </defs>
    <polygon points="50,3 93,28 93,72 50,97 7,72 7,28"
      fill="none" stroke="url(#hexGrad)" strokeWidth="4" />
    <text x="50" y="60" textAnchor="middle" fill="url(#hexGrad)"
      fontFamily="Orbitron" fontWeight="800" fontSize="28">CX</text>
  </svg>
);

/* ═══════════════════════════════════════════
   NAVBAR
   ═══════════════════════════════════════════ */
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/ecosystem', label: 'Ecosystem' },
    { path: '/developers', label: 'Developers' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <nav style={{
      position:'fixed', top:0, left:0, right:0, zIndex:1000,
      background: scrolled ? 'rgba(3,7,18,0.95)' : 'transparent',
      backdropFilter: scrolled ? 'blur(20px)' : 'none',
      borderBottom: scrolled ? '1px solid var(--border)' : 'none',
      transition:'all 0.4s ease',
      padding: '0 5%',
    }}>
      <div style={{
        maxWidth:1280, margin:'0 auto', display:'flex', alignItems:'center',
        justifyContent:'space-between', height: scrolled ? 64 : 80,
        transition:'height 0.4s ease',
      }}>
        <Link to="/" style={{ display:'flex', alignItems:'center', gap:12 }}>
          <HexLogo size={36} />
          <span style={{ fontFamily:'Orbitron', fontWeight:700, fontSize:20, color:'var(--primary)', letterSpacing:2 }}>
            COREX
          </span>
        </Link>

        {/* Desktop Menu */}
        <div style={{ display:'flex', gap:32, alignItems:'center' }}
          className="desktop-nav">
          {navLinks.map(l => (
            <Link key={l.path} to={l.path} style={{
              fontFamily:'Rajdhani', fontWeight:600, fontSize:15, letterSpacing:1.5,
              textTransform:'uppercase',
              color: location.pathname === l.path ? 'var(--primary)' : 'var(--text-dim)',
              transition:'color 0.3s',
              position:'relative',
            }}>
              {l.label}
              {location.pathname === l.path && (
                <span style={{
                  position:'absolute', bottom:-4, left:0, right:0, height:2,
                  background:'var(--primary)', borderRadius:1,
                }} />
              )}
            </Link>
          ))}
          <a href="https://bricsx.io" target="_blank" rel="noopener noreferrer" style={{
            padding:'10px 24px', borderRadius:8,
            background:'linear-gradient(135deg, #00f0ff, #a855f7)',
            fontFamily:'Orbitron', fontWeight:600, fontSize:12,
            color:'#030712', letterSpacing:1, textTransform:'uppercase',
            transition:'transform 0.3s, box-shadow 0.3s',
          }}
          onMouseOver={e=>{e.target.style.transform='translateY(-2px)';e.target.style.boxShadow='0 8px 30px rgba(0,240,255,0.4)';}}
          onMouseOut={e=>{e.target.style.transform='translateY(0)';e.target.style.boxShadow='none';}}
          >
            Launch App
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button onClick={()=>setMenuOpen(!menuOpen)} style={{
          display:'none', background:'none', border:'none', cursor:'pointer',
          flexDirection:'column', gap:5, padding:8,
        }} className="mobile-toggle">
          <span style={{ width:24, height:2, background:'var(--primary)', borderRadius:2,
            transition:'all 0.3s', transform: menuOpen ? 'rotate(45deg) translateY(7px)' : 'none' }} />
          <span style={{ width:24, height:2, background:'var(--primary)', borderRadius:2,
            transition:'all 0.3s', opacity: menuOpen ? 0 : 1 }} />
          <span style={{ width:24, height:2, background:'var(--primary)', borderRadius:2,
            transition:'all 0.3s', transform: menuOpen ? 'rotate(-45deg) translateY(-7px)' : 'none' }} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div style={{
          position:'absolute', top:'100%', left:0, right:0,
          background:'rgba(3,7,18,0.98)', backdropFilter:'blur(20px)',
          borderBottom:'1px solid var(--border)',
          padding:'20px 5%', display:'flex', flexDirection:'column', gap:16,
          animation:'slideUp 0.3s ease',
        }}>
          {navLinks.map(l => (
            <Link key={l.path} to={l.path} style={{
              fontFamily:'Rajdhani', fontWeight:600, fontSize:18, letterSpacing:1,
              color: location.pathname === l.path ? 'var(--primary)' : 'var(--text-dim)',
              padding:'8px 0', borderBottom:'1px solid var(--border)',
            }}>
              {l.label}
            </Link>
          ))}
        </div>
      )}

      <style>{`
        @media(max-width:768px) {
          .desktop-nav { display:none !important; }
          .mobile-toggle { display:flex !important; }
        }
      `}</style>
    </nav>
  );
};

/* ═══════════════════════════════════════════
   FOOTER
   ═══════════════════════════════════════════ */
const Footer = () => (
  <footer style={{
    background:'rgba(3,7,18,0.95)', borderTop:'1px solid var(--border)',
    padding:'60px 5% 30px', position:'relative',
  }}>
    <div style={{ maxWidth:1280, margin:'0 auto' }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:40, marginBottom:40 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
            <HexLogo size={30} />
            <span style={{ fontFamily:'Orbitron', fontWeight:700, fontSize:18, color:'var(--primary)' }}>COREX NETWORK</span>
          </div>
          <p style={{ color:'var(--text-dim)', fontSize:14, lineHeight:1.8 }}>
            Next-generation blockchain powering the CRX-20 token standard.
            Fast, secure, and scalable.
          </p>
        </div>
        <div>
          <h4 style={{ fontFamily:'Orbitron', fontSize:13, color:'var(--primary)', letterSpacing:2, marginBottom:16 }}>NETWORK</h4>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {['CRX-20 Standard','Block Explorer','Network Status','Validators'].map(t=>(
              <span key={t} style={{ color:'var(--text-dim)', fontSize:14, cursor:'pointer', transition:'color 0.3s' }}
                onMouseOver={e=>e.target.style.color='var(--primary)'}
                onMouseOut={e=>e.target.style.color='var(--text-dim)'}>{t}</span>
            ))}
          </div>
        </div>
        <div>
          <h4 style={{ fontFamily:'Orbitron', fontSize:13, color:'var(--primary)', letterSpacing:2, marginBottom:16 }}>ECOSYSTEM</h4>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {['BRX Token','BRICSX.io','Wallet','DApps'].map(t=>(
              <span key={t} style={{ color:'var(--text-dim)', fontSize:14, cursor:'pointer', transition:'color 0.3s' }}
                onMouseOver={e=>e.target.style.color='var(--primary)'}
                onMouseOut={e=>e.target.style.color='var(--text-dim)'}>{t}</span>
            ))}
          </div>
        </div>
        <div>
          <h4 style={{ fontFamily:'Orbitron', fontSize:13, color:'var(--primary)', letterSpacing:2, marginBottom:16 }}>CONNECT</h4>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {['Twitter / X','Telegram','Discord','GitHub'].map(t=>(
              <span key={t} style={{ color:'var(--text-dim)', fontSize:14, cursor:'pointer', transition:'color 0.3s' }}
                onMouseOver={e=>e.target.style.color='var(--primary)'}
                onMouseOut={e=>e.target.style.color='var(--text-dim)'}>{t}</span>
            ))}
          </div>
        </div>
      </div>
      <div style={{ borderTop:'1px solid var(--border)', paddingTop:24, display:'flex',
        flexWrap:'wrap', justifyContent:'space-between', alignItems:'center', gap:16 }}>
        <p style={{ color:'var(--text-dim)', fontSize:13 }}>
          © 2024 COREX Network. All rights reserved. Powered by <a href="https://bricsx.io" target="_blank" rel="noopener noreferrer" style={{ color:'var(--primary)' }}>BRICSX.io</a>
        </p>
        <div style={{ display:'flex', gap:20 }}>
          {['Privacy','Terms','Docs'].map(t=>(
            <span key={t} style={{ color:'var(--text-dim)', fontSize:13, cursor:'pointer' }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

/* ═══════════════════════════════════════════
   REUSABLE COMPONENTS
   ═══════════════════════════════════════════ */
const SectionTitle = ({ tag, title, subtitle }) => (
  <div className="animate-on-scroll" style={{ textAlign:'center', marginBottom:60 }}>
    {tag && <span style={{
      fontFamily:'Space Mono', fontSize:12, color:'var(--primary)', letterSpacing:3,
      textTransform:'uppercase', display:'inline-block', padding:'6px 16px',
      border:'1px solid var(--border)', borderRadius:20, marginBottom:16,
    }}>{tag}</span>}
    <h2 style={{ fontFamily:'Orbitron', fontSize:'clamp(28px,4vw,48px)', fontWeight:800,
      background:'linear-gradient(135deg, #fff 0%, var(--primary) 100%)',
      WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
      lineHeight:1.2, marginBottom:16 }}>{title}</h2>
    {subtitle && <p style={{ color:'var(--text-dim)', fontSize:18, maxWidth:600, margin:'0 auto' }}>{subtitle}</p>}
  </div>
);

const GlowCard = ({ children, style = {}, delay = 0 }) => (
  <div className="animate-on-scroll" style={{
    background:'var(--bg-card)', border:'1px solid var(--border)',
    borderRadius:16, padding:32, position:'relative', overflow:'hidden',
    transition:'all 0.4s ease', cursor:'default',
    transitionDelay: `${delay}ms`,
    ...style
  }}
  onMouseOver={e=>{
    e.currentTarget.style.borderColor='rgba(0,240,255,0.4)';
    e.currentTarget.style.boxShadow='0 8px 40px rgba(0,240,255,0.15)';
    e.currentTarget.style.transform='translateY(-4px)';
  }}
  onMouseOut={e=>{
    e.currentTarget.style.borderColor='var(--border)';
    e.currentTarget.style.boxShadow='none';
    e.currentTarget.style.transform='translateY(0)';
  }}>
    {children}
  </div>
);

const StatCounter = ({ value, label, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0;
        const end = parseInt(value);
        const duration = 2000;
        const step = end / (duration / 16);
        const timer = setInterval(() => {
          start += step;
          if (start >= end) { setCount(end); clearInterval(timer); }
          else setCount(Math.floor(start));
        }, 16);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);
  return (
    <div ref={ref} style={{ textAlign:'center' }}>
      <div style={{ fontFamily:'Orbitron', fontSize:'clamp(32px,5vw,56px)', fontWeight:800, color:'var(--primary)' }}>
        {count.toLocaleString()}{suffix}
      </div>
      <div style={{ fontFamily:'Rajdhani', fontSize:16, color:'var(--text-dim)', letterSpacing:1, marginTop:8 }}>{label}</div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   PAGE 1: HOME
   ═══════════════════════════════════════════ */
const HomePage = () => {
  useScrollAnimation();

  return (
    <div>
      {/* HERO */}
      <section style={{
        minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
        position:'relative', padding:'120px 5% 80px', overflow:'hidden',
      }}>
        {/* Gradient orbs */}
        <div style={{
          position:'absolute', width:600, height:600, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(0,240,255,0.12) 0%, transparent 70%)',
          top:'-10%', right:'-10%', animation:'float 8s ease-in-out infinite',
        }} />
        <div style={{
          position:'absolute', width:400, height:400, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(168,85,247,0.1) 0%, transparent 70%)',
          bottom:'10%', left:'-5%', animation:'float 10s ease-in-out infinite 2s',
        }} />

        <div style={{ textAlign:'center', maxWidth:900, position:'relative', zIndex:1 }}>
          <div style={{ animation:'fadeIn 1s ease', marginBottom:24 }}>
            <span style={{
              fontFamily:'Space Mono', fontSize:13, color:'var(--primary)', letterSpacing:4,
              textTransform:'uppercase', display:'inline-flex', alignItems:'center', gap:8,
              padding:'8px 20px', border:'1px solid var(--border)', borderRadius:24,
              animation:'borderGlow 3s ease-in-out infinite',
            }}>
              <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--primary)', animation:'pulse 2s infinite' }} />
              MAINNET LIVE
            </span>
          </div>

          <h1 style={{
            fontFamily:'Orbitron', fontSize:'clamp(36px,7vw,80px)', fontWeight:900,
            lineHeight:1.1, marginBottom:24,
            animation:'slideUp 1s ease 0.2s both',
          }}>
            <span style={{ color:'#fff' }}>THE FUTURE OF</span><br/>
            <span style={{
              background:'linear-gradient(135deg, #00f0ff 0%, #a855f7 50%, #ff6b35 100%)',
              WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
            }}>BLOCKCHAIN</span>
          </h1>

          <p style={{
            fontSize:'clamp(16px,2vw,22px)', color:'var(--text-dim)', maxWidth:650,
            margin:'0 auto 40px', lineHeight:1.7,
            animation:'slideUp 1s ease 0.4s both',
          }}>
            COREX Network — A high-performance Layer 1 blockchain with CRX-20 token standard.
            Transfer BRX tokens with lightning speed and minimal fees.
          </p>

          <div style={{ display:'flex', gap:16, justifyContent:'center', flexWrap:'wrap', animation:'slideUp 1s ease 0.6s both' }}>
            <Link to="/ecosystem" style={{
              padding:'14px 36px', borderRadius:10,
              background:'linear-gradient(135deg, #00f0ff, #a855f7)',
              fontFamily:'Orbitron', fontWeight:700, fontSize:14,
              color:'#030712', letterSpacing:1, textTransform:'uppercase',
              transition:'all 0.3s',
            }}
            onMouseOver={e=>e.target.style.boxShadow='0 8px 40px rgba(0,240,255,0.4)'}
            onMouseOut={e=>e.target.style.boxShadow='none'}
            >
              Explore Ecosystem
            </Link>
            <Link to="/developers" style={{
              padding:'14px 36px', borderRadius:10,
              border:'1px solid var(--primary)',
              fontFamily:'Orbitron', fontWeight:700, fontSize:14,
              color:'var(--primary)', letterSpacing:1, textTransform:'uppercase',
              transition:'all 0.3s',
            }}
            onMouseOver={e=>{e.target.style.background='rgba(0,240,255,0.1)';e.target.style.boxShadow='0 8px 40px rgba(0,240,255,0.2)';}}
            onMouseOut={e=>{e.target.style.background='transparent';e.target.style.boxShadow='none';}}
            >
              Start Building
            </Link>
          </div>

          {/* Animated hex grid */}
          <div style={{ marginTop:60, display:'flex', justifyContent:'center', gap:20, flexWrap:'wrap' }}>
            {['CRX-20','BRX TOKEN','DAPPS','DEFI','NFTs'].map((t,i) => (
              <div key={t} style={{
                padding:'10px 20px', border:'1px solid var(--border)', borderRadius:8,
                fontFamily:'Space Mono', fontSize:11, color:'var(--text-dim)',
                letterSpacing:2, animation:`fadeIn 0.5s ease ${0.8 + i*0.15}s both`,
              }}>{t}</div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section style={{ padding:'60px 5%', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)' }}>
        <div style={{ maxWidth:1280, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:40 }}>
          <StatCounter value="50000" label="Transactions / Sec" suffix="+" />
          <StatCounter value="150" label="Global Validators" suffix="+" />
          <StatCounter value="99" label="Uptime %" suffix=".99" />
          <StatCounter value="1" label="Avg Block Time" suffix="s" />
        </div>
      </section>

      {/* WHY COREX */}
      <section style={{ padding:'100px 5%' }}>
        <div style={{ maxWidth:1280, margin:'0 auto' }}>
          <SectionTitle tag="Why COREX" title="Built for the Next Era"
            subtitle="COREX Network combines speed, security, and scalability to power the future of decentralized applications." />

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:24 }}>
            {[
              { icon:'⚡', title:'Lightning Fast', desc:'50,000+ TPS with 1-second block finality. Send BRX tokens across the globe in under a second.' },
              { icon:'🛡️', title:'Battle-Tested Security', desc:'Advanced consensus mechanism with 150+ validators securing the network 24/7.' },
              { icon:'💰', title:'Near-Zero Fees', desc:'Transaction fees as low as $0.001. Send CRX-20 tokens without burning through your wallet.' },
              { icon:'🔗', title:'CRX-20 Standard', desc:'Like BEP-20 for BSC and TRC-20 for TRON — CRX-20 is the native token standard on COREX Network.' },
              { icon:'🌐', title:'Cross-Chain Ready', desc:'Built-in bridges to Ethereum, BSC, and TRON. Move assets seamlessly across chains.' },
              { icon:'♻️', title:'Eco-Friendly', desc:'Proof-of-Stake consensus uses 99.9% less energy than traditional PoW blockchains.' },
            ].map((item, i) => (
              <GlowCard key={i} delay={i * 100}>
                <div style={{ fontSize:40, marginBottom:16 }}>{item.icon}</div>
                <h3 style={{ fontFamily:'Orbitron', fontSize:18, fontWeight:700, marginBottom:12, color:'#fff' }}>{item.title}</h3>
                <p style={{ color:'var(--text-dim)', fontSize:15, lineHeight:1.7 }}>{item.desc}</p>
              </GlowCard>
            ))}
          </div>
        </div>
      </section>

      {/* CRX-20 vs Others Comparison */}
      <section style={{ padding:'100px 5%', background:'rgba(0,240,255,0.02)' }}>
        <div style={{ maxWidth:1000, margin:'0 auto' }}>
          <SectionTitle tag="Comparison" title="CRX-20 vs Others"
            subtitle="See how COREX Network's CRX-20 standard stacks up against the competition." />

          <div className="animate-on-scroll" style={{
            borderRadius:16, overflow:'hidden', border:'1px solid var(--border)',
          }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:'Rajdhani' }}>
              <thead>
                <tr style={{ background:'rgba(0,240,255,0.08)' }}>
                  {['Feature','CRX-20 (COREX)','BEP-20 (BSC)','TRC-20 (TRON)','ERC-20 (ETH)'].map(h=>(
                    <th key={h} style={{
                      padding:'16px 20px', textAlign:'left', fontFamily:'Orbitron', fontSize:12,
                      color: h.includes('CRX') ? 'var(--primary)' : 'var(--text-dim)',
                      letterSpacing:1, borderBottom:'1px solid var(--border)',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['TPS','50,000+','160','2,000','30'],
                  ['Block Time','1s','3s','3s','12s'],
                  ['Avg Fee','$0.001','$0.10','$0.05','$5-50'],
                  ['Finality','Instant','~45s','~60s','~6 min'],
                  ['Consensus','DPoS+','DPoS','DPoS','PoS'],
                ].map((row,i)=>(
                  <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                    {row.map((cell,j)=>(
                      <td key={j} style={{
                        padding:'14px 20px', fontSize:14,
                        color: j===1 ? 'var(--primary)' : 'var(--text-dim)',
                        fontWeight: j===1 ? 600 : 400,
                        fontFamily: j===0 ? 'Orbitron' : 'Rajdhani',
                        letterSpacing: j===0 ? 1 : 0,
                      }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding:'100px 5%' }}>
        <div className="animate-on-scroll" style={{
          maxWidth:900, margin:'0 auto', textAlign:'center',
          background:'linear-gradient(135deg, rgba(0,240,255,0.08), rgba(168,85,247,0.08))',
          border:'1px solid var(--border)', borderRadius:24, padding:'60px 40px',
        }}>
          <h2 style={{ fontFamily:'Orbitron', fontSize:'clamp(24px,4vw,40px)', fontWeight:800, color:'#fff', marginBottom:16 }}>
            Ready to Build on COREX?
          </h2>
          <p style={{ color:'var(--text-dim)', fontSize:18, marginBottom:32, maxWidth:500, margin:'0 auto 32px' }}>
            Join the growing ecosystem of developers and projects building on the CRX-20 standard.
          </p>
          <div style={{ display:'flex', gap:16, justifyContent:'center', flexWrap:'wrap' }}>
            <a href="https://bricsx.io" target="_blank" rel="noopener noreferrer" style={{
              padding:'14px 36px', borderRadius:10,
              background:'linear-gradient(135deg, #00f0ff, #a855f7)',
              fontFamily:'Orbitron', fontWeight:700, fontSize:14,
              color:'#030712', letterSpacing:1, textTransform:'uppercase',
            }}>Get BRX Tokens</a>
            <Link to="/developers" style={{
              padding:'14px 36px', borderRadius:10,
              border:'1px solid var(--primary)',
              fontFamily:'Orbitron', fontWeight:700, fontSize:14,
              color:'var(--primary)', letterSpacing:1, textTransform:'uppercase',
            }}>Read Docs</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════
   PAGE 2: ABOUT
   ═══════════════════════════════════════════ */
const AboutPage = () => {
  useScrollAnimation();
  return (
    <div style={{ paddingTop:100 }}>
      {/* Hero */}
      <section style={{ padding:'80px 5%', textAlign:'center' }}>
        <div style={{ maxWidth:800, margin:'0 auto' }}>
          <SectionTitle tag="About Us" title="The COREX Network Story"
            subtitle="Building a faster, fairer, and more accessible blockchain for everyone." />
        </div>
      </section>

      {/* Mission */}
      <section style={{ padding:'40px 5% 100px' }}>
        <div style={{ maxWidth:1280, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:40 }}>
          <GlowCard>
            <div style={{ fontFamily:'Orbitron', fontSize:14, color:'var(--primary)', letterSpacing:2, marginBottom:16 }}>OUR MISSION</div>
            <h3 style={{ fontFamily:'Orbitron', fontSize:24, fontWeight:700, color:'#fff', marginBottom:16 }}>
              Democratizing Blockchain
            </h3>
            <p style={{ color:'var(--text-dim)', fontSize:16, lineHeight:1.8 }}>
              COREX Network was built with one goal: to make blockchain technology accessible to everyone.
              Just as Tether brought stability to crypto with USDT, BRICSX.io brings real-world utility
              through BRX tokens on the CRX-20 standard. We believe the future of finance is decentralized,
              fast, and affordable.
            </p>
          </GlowCard>
          <GlowCard delay={200}>
            <div style={{ fontFamily:'Orbitron', fontSize:14, color:'var(--secondary)', letterSpacing:2, marginBottom:16 }}>OUR VISION</div>
            <h3 style={{ fontFamily:'Orbitron', fontSize:24, fontWeight:700, color:'#fff', marginBottom:16 }}>
              CRX-20: A New Standard
            </h3>
            <p style={{ color:'var(--text-dim)', fontSize:16, lineHeight:1.8 }}>
              BEP-20 powers Binance Smart Chain. TRC-20 powers TRON. CRX-20 powers COREX Network.
              Our token standard is designed from the ground up to support high-throughput transactions,
              smart contracts, and DeFi applications. BRX is the flagship token, but CRX-20 is open
              for anyone to create tokens on the COREX blockchain.
            </p>
          </GlowCard>
        </div>
      </section>

      {/* How It Works */}
      <section style={{ padding:'100px 5%', background:'rgba(0,240,255,0.02)' }}>
        <div style={{ maxWidth:1000, margin:'0 auto' }}>
          <SectionTitle tag="How It Works" title="From BRICSX to You"
            subtitle="Understanding the COREX ecosystem and how BRX tokens flow." />

          <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
            {[
              { step:'01', title:'BRICSX.io Issues BRX', desc:'Just like Tether issues USDT, BRICSX.io mints and issues BRX tokens on the COREX Network using the CRX-20 standard.', color:'var(--primary)' },
              { step:'02', title:'BRX Lives on CRX-20', desc:'BRX tokens are CRX-20 tokens — the native standard of COREX Network. Think of it as USDT living on TRC-20 or BEP-20.', color:'var(--accent)' },
              { step:'03', title:'Transfer BRX Instantly', desc:'Send BRX to anyone on the COREX Network with 1-second finality and near-zero fees. Fast, secure, final.', color:'var(--secondary)' },
              { step:'04', title:'Use in DApps & DeFi', desc:'BRX can be used across the growing COREX ecosystem — DEXs, lending platforms, NFT markets, and more.', color:'#22c55e' },
            ].map((item, i) => (
              <div key={i} className="animate-on-scroll" style={{
                display:'flex', gap:32, alignItems:'flex-start', padding:'32px 0',
                borderLeft:`2px solid ${item.color}`, paddingLeft:32, position:'relative',
                marginLeft:20,
              }}>
                <div style={{
                  position:'absolute', left:-13, top:32, width:24, height:24,
                  borderRadius:'50%', background:item.color,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontFamily:'Orbitron', fontSize:10, fontWeight:800, color:'#030712',
                }}>{item.step}</div>
                <div>
                  <h3 style={{ fontFamily:'Orbitron', fontSize:20, fontWeight:700, color:'#fff', marginBottom:8 }}>
                    {item.title}
                  </h3>
                  <p style={{ color:'var(--text-dim)', fontSize:16, lineHeight:1.7 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team / Company */}
      <section style={{ padding:'100px 5%' }}>
        <div style={{ maxWidth:1280, margin:'0 auto' }}>
          <SectionTitle tag="Powered By" title="BRICSX.io" subtitle="The company behind COREX Network and BRX tokens." />
          <div className="animate-on-scroll" style={{
            background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:20,
            padding:'48px', textAlign:'center', maxWidth:700, margin:'0 auto',
          }}>
            <div style={{
              width:80, height:80, borderRadius:'50%', margin:'0 auto 24px',
              background:'linear-gradient(135deg, #00f0ff, #a855f7)',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontFamily:'Orbitron', fontSize:28, fontWeight:900, color:'#030712',
            }}>B</div>
            <h3 style={{ fontFamily:'Orbitron', fontSize:28, fontWeight:700, color:'#fff', marginBottom:12 }}>BRICSX</h3>
            <p style={{ color:'var(--text-dim)', fontSize:16, lineHeight:1.8, marginBottom:24 }}>
              BRICSX.io is the organization behind COREX Network. Just as Tether Limited issues USDT,
              BRICSX issues BRX — a utility token designed for fast, low-cost transfers on the CRX-20 standard.
              Visit <a href="https://bricsx.io" target="_blank" rel="noopener noreferrer" style={{ color:'var(--primary)' }}>bricsx.io</a> to learn more.
            </p>
            <a href="https://bricsx.io" target="_blank" rel="noopener noreferrer" style={{
              display:'inline-block', padding:'12px 32px', borderRadius:10,
              background:'linear-gradient(135deg, #00f0ff, #a855f7)',
              fontFamily:'Orbitron', fontWeight:700, fontSize:13,
              color:'#030712', letterSpacing:1, textTransform:'uppercase',
            }}>Visit BRICSX.io</a>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════
   PAGE 3: ECOSYSTEM
   ═══════════════════════════════════════════ */
const EcosystemPage = () => {
  useScrollAnimation();
  return (
    <div style={{ paddingTop:100 }}>
      <section style={{ padding:'80px 5%' }}>
        <SectionTitle tag="Ecosystem" title="The COREX Universe"
          subtitle="Everything built on CRX-20 — from DeFi to NFTs and beyond." />

        {/* BRX Token Highlight */}
        <div className="animate-on-scroll" style={{
          maxWidth:1000, margin:'0 auto 60px',
          background:'linear-gradient(135deg, rgba(0,240,255,0.08), rgba(168,85,247,0.08))',
          border:'1px solid var(--border)', borderRadius:20, padding:'48px',
          display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:40, alignItems:'center',
        }}>
          <div>
            <span style={{ fontFamily:'Space Mono', fontSize:12, color:'var(--primary)', letterSpacing:3 }}>FLAGSHIP TOKEN</span>
            <h3 style={{ fontFamily:'Orbitron', fontSize:'clamp(28px,4vw,42px)', fontWeight:800, color:'#fff', margin:'12px 0 16px' }}>
              BRX
            </h3>
            <p style={{ color:'var(--text-dim)', fontSize:16, lineHeight:1.8, marginBottom:24 }}>
              BRX is the primary utility token on COREX Network, issued by BRICSX.io.
              It runs on the CRX-20 standard — just like USDT runs on TRC-20 and BEP-20.
              Use BRX for transfers, DeFi, payments, and more.
            </p>
            <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              {['CRX-20','Utility','Payments','DeFi'].map(t=>(
                <span key={t} style={{
                  padding:'6px 14px', borderRadius:6, border:'1px solid var(--border)',
                  fontFamily:'Space Mono', fontSize:11, color:'var(--primary)', letterSpacing:1,
                }}>{t}</span>
              ))}
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            {[
              { label:'Standard', value:'CRX-20' },
              { label:'Network', value:'COREX' },
              { label:'Issuer', value:'BRICSX.io' },
              { label:'Type', value:'Utility' },
            ].map(item=>(
              <div key={item.label} style={{
                background:'rgba(3,7,18,0.6)', borderRadius:12, padding:'16px',
                border:'1px solid var(--border)',
              }}>
                <div style={{ fontFamily:'Rajdhani', fontSize:13, color:'var(--text-dim)', marginBottom:4 }}>{item.label}</div>
                <div style={{ fontFamily:'Orbitron', fontSize:16, fontWeight:700, color:'var(--primary)' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Ecosystem Cards */}
        <div style={{ maxWidth:1280, margin:'0 auto' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:24 }}>
            {[
              { icon:'🔄', title:'COREX DEX', desc:'Decentralized exchange for swapping CRX-20 tokens. Trade BRX and other tokens with minimal slippage.', status:'Live' },
              { icon:'💳', title:'COREX Wallet', desc:'Official wallet supporting all CRX-20 tokens. Secure, fast, and easy to use. Available on web and mobile.', status:'Live' },
              { icon:'🏦', title:'COREX Lending', desc:'Lend and borrow CRX-20 tokens. Earn yield on your BRX holdings through our DeFi lending protocol.', status:'Coming Soon' },
              { icon:'🖼️', title:'COREX NFTs', desc:'Mint, buy, and sell NFTs on the COREX Network. Ultra-low fees make NFTs accessible to everyone.', status:'Coming Soon' },
              { icon:'🌉', title:'Cross-Chain Bridge', desc:'Bridge assets between COREX, Ethereum, BSC, and TRON. Move your tokens across chains seamlessly.', status:'Beta' },
              { icon:'🔍', title:'Block Explorer', desc:'Track transactions, blocks, and addresses on the COREX Network. Full transparency for all CRX-20 activity.', status:'Live' },
              { icon:'🎮', title:'COREX Gaming', desc:'Play-to-earn gaming powered by CRX-20 tokens. Earn BRX while playing your favorite games.', status:'Coming Soon' },
              { icon:'📊', title:'COREX Analytics', desc:'Real-time analytics dashboard for the COREX ecosystem. Track volume, TVL, and network health.', status:'Live' },
            ].map((item, i) => (
              <GlowCard key={i} delay={i * 80}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
                  <div style={{ fontSize:36 }}>{item.icon}</div>
                  <span style={{
                    padding:'4px 10px', borderRadius:6, fontSize:11, fontFamily:'Space Mono',
                    letterSpacing:1,
                    background: item.status === 'Live' ? 'rgba(34,197,94,0.15)' : item.status === 'Beta' ? 'rgba(168,85,247,0.15)' : 'rgba(255,107,53,0.15)',
                    color: item.status === 'Live' ? '#22c55e' : item.status === 'Beta' ? '#a855f7' : '#ff6b35',
                    border: `1px solid ${item.status === 'Live' ? 'rgba(34,197,94,0.3)' : item.status === 'Beta' ? 'rgba(168,85,247,0.3)' : 'rgba(255,107,53,0.3)'}`,
                  }}>{item.status}</span>
                </div>
                <h3 style={{ fontFamily:'Orbitron', fontSize:17, fontWeight:700, color:'#fff', marginBottom:10 }}>{item.title}</h3>
                <p style={{ color:'var(--text-dim)', fontSize:14, lineHeight:1.7 }}>{item.desc}</p>
              </GlowCard>
            ))}
          </div>
        </div>
      </section>

      {/* Token Flow Diagram */}
      <section style={{ padding:'80px 5%', background:'rgba(0,240,255,0.02)' }}>
        <div style={{ maxWidth:800, margin:'0 auto' }}>
          <SectionTitle tag="Token Flow" title="How BRX Moves"
            subtitle="Understanding the flow of BRX tokens on the COREX Network." />
          <div className="animate-on-scroll" style={{
            display:'flex', flexDirection:'column', alignItems:'center', gap:0,
          }}>
            {[
              { label:'BRICSX.io', sub:'Token Issuer', color:'var(--primary)' },
              { label:'CRX-20 Contract', sub:'Smart Contract', color:'var(--accent)' },
              { label:'COREX Network', sub:'Blockchain', color:'var(--secondary)' },
              { label:'Your Wallet', sub:'End User', color:'#22c55e' },
            ].map((item, i) => (
              <React.Fragment key={i}>
                <div style={{
                  padding:'20px 40px', borderRadius:12, border:`2px solid ${item.color}`,
                  background:'var(--bg-card)', textAlign:'center', minWidth:260,
                }}>
                  <div style={{ fontFamily:'Orbitron', fontSize:16, fontWeight:700, color:item.color }}>{item.label}</div>
                  <div style={{ fontFamily:'Rajdhani', fontSize:13, color:'var(--text-dim)', marginTop:4 }}>{item.sub}</div>
                </div>
                {i < 3 && (
                  <div style={{ width:2, height:40, background:`linear-gradient(to bottom, ${item.color}, ${['var(--accent)','var(--secondary)','#22c55e'][i]})` }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════
   PAGE 4: DEVELOPERS
   ═══════════════════════════════════════════ */
const DevelopersPage = () => {
  useScrollAnimation();
  const [activeTab, setActiveTab] = useState(0);

  const codeExamples = [
    {
      title: 'Transfer BRX',
      lang: 'JavaScript',
      code: `// Transfer BRX on CRX-20
const corex = require('@corex/sdk');

const wallet = corex.Wallet.fromPrivateKey(
  process.env.PRIVATE_KEY
);

const tx = await wallet.transfer({
  token: 'BRX',           // CRX-20 token
  to: '0xRecipientAddress',
  amount: '100.00',        // 100 BRX
  standard: 'CRX-20'
});

console.log('TX Hash:', tx.hash);
// Confirmed in ~1 second!`
    },
    {
      title: 'Deploy CRX-20 Token',
      lang: 'Solidity',
      code: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.19;

import "@corex/contracts/CRX20.sol";

contract MyToken is CRX20 {
    constructor() CRX20(
        "My Token",    // Name
        "MTK",         // Symbol
        18,            // Decimals
        1000000        // Total Supply
    ) {
        // Your CRX-20 token is live!
    }
}`
    },
    {
      title: 'Read Balance',
      lang: 'JavaScript',
      code: `// Check BRX balance on COREX Network
const corex = require('@corex/sdk');

const provider = new corex.Provider(
  'https://rpc.corexnetwork.org'
);

const balance = await provider.getBalance(
  '0xYourAddress',
  'BRX'  // CRX-20 token symbol
);

console.log('BRX Balance:', balance);
// Output: "1,250.00 BRX"`
    }
  ];

  return (
    <div style={{ paddingTop:100 }}>
      <section style={{ padding:'80px 5%' }}>
        <SectionTitle tag="Developers" title="Build on COREX"
          subtitle="Everything you need to build dApps, deploy CRX-20 tokens, and integrate with the COREX Network." />

        {/* Network Config */}
        <div className="animate-on-scroll" style={{
          maxWidth:700, margin:'0 auto 60px',
          background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:16, padding:'32px',
        }}>
          <h3 style={{ fontFamily:'Orbitron', fontSize:16, fontWeight:700, color:'var(--primary)', marginBottom:20, letterSpacing:1 }}>
            ⚙️ NETWORK CONFIGURATION
          </h3>
          <div style={{ display:'grid', gap:12 }}>
            {[
              ['Network Name', 'COREX Network Mainnet'],
              ['Chain ID', '7201'],
              ['RPC URL', 'https://rpc.corexnetwork.org'],
              ['WebSocket', 'wss://ws.corexnetwork.org'],
              ['Explorer', 'https://explorer.corexnetwork.org'],
              ['Token Standard', 'CRX-20'],
              ['Native Token', 'CRX'],
              ['Symbol', 'CRX'],
            ].map(([k, v]) => (
              <div key={k} style={{
                display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'10px 16px', background:'rgba(3,7,18,0.6)', borderRadius:8,
                border:'1px solid var(--border)',
              }}>
                <span style={{ fontFamily:'Rajdhani', fontSize:14, color:'var(--text-dim)' }}>{k}</span>
                <span style={{ fontFamily:'Space Mono', fontSize:13, color:'var(--primary)' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Code Examples */}
        <div style={{ maxWidth:800, margin:'0 auto 60px' }}>
          <div className="animate-on-scroll" style={{
            display:'flex', gap:8, marginBottom:0,
            background:'rgba(3,7,18,0.6)', borderRadius:'16px 16px 0 0',
            border:'1px solid var(--border)', borderBottom:'none',
            padding:'8px',
          }}>
            {codeExamples.map((ex, i) => (
              <button key={i} onClick={() => setActiveTab(i)} style={{
                padding:'10px 20px', borderRadius:8, border:'none', cursor:'pointer',
                fontFamily:'Rajdhani', fontWeight:600, fontSize:14,
                background: activeTab === i ? 'rgba(0,240,255,0.15)' : 'transparent',
                color: activeTab === i ? 'var(--primary)' : 'var(--text-dim)',
                transition:'all 0.3s',
              }}>{ex.title}</button>
            ))}
          </div>
          <div style={{
            background:'rgba(3,7,18,0.8)', border:'1px solid var(--border)',
            borderRadius:'0 0 16px 16px', padding:'24px', overflow:'auto',
          }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <span style={{ fontFamily:'Space Mono', fontSize:12, color:'var(--text-dim)' }}>
                {codeExamples[activeTab].lang}
              </span>
              <span style={{
                padding:'4px 12px', borderRadius:4, fontSize:11,
                fontFamily:'Space Mono', color:'var(--primary)',
                border:'1px solid var(--border)', cursor:'pointer',
              }}>Copy</span>
            </div>
            <pre style={{
              fontFamily:'Space Mono', fontSize:13, lineHeight:1.8,
              color:'var(--text)', whiteSpace:'pre-wrap', margin:0,
            }}>
              {codeExamples[activeTab].code}
            </pre>
          </div>
        </div>

        {/* Developer Resources */}
        <div style={{ maxWidth:1280, margin:'0 auto' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:24 }}>
            {[
              { icon:'📖', title:'Documentation', desc:'Complete guides for building on COREX Network. From setup to deployment.', link:'Read Docs →' },
              { icon:'🔧', title:'SDK & Tools', desc:'Official SDKs for JavaScript, Python, and Go. CLI tools for deployment and testing.', link:'Get Started →' },
              { icon:'🧪', title:'Testnet', desc:'Free testnet CRX for development. Deploy and test your CRX-20 tokens risk-free.', link:'Get Testnet CRX →' },
              { icon:'💬', title:'Developer Community', desc:'Join our Discord and Telegram for support, discussions, and hackathons.', link:'Join Community →' },
              { icon:'🏆', title:'Grants Program', desc:'Get funded to build on COREX. Up to $50K for innovative dApps and tools.', link:'Apply Now →' },
              { icon:'🔐', title:'Security Audits', desc:'Our smart contracts are audited. Use our security tools for your own contracts.', link:'Learn More →' },
            ].map((item, i) => (
              <GlowCard key={i} delay={i * 100}>
                <div style={{ fontSize:36, marginBottom:16 }}>{item.icon}</div>
                <h3 style={{ fontFamily:'Orbitron', fontSize:17, fontWeight:700, color:'#fff', marginBottom:10 }}>{item.title}</h3>
                <p style={{ color:'var(--text-dim)', fontSize:14, lineHeight:1.7, marginBottom:16 }}>{item.desc}</p>
                <span style={{ fontFamily:'Orbitron', fontSize:12, color:'var(--primary)', letterSpacing:1, cursor:'pointer' }}>{item.link}</span>
              </GlowCard>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════
   PAGE 5: CONTACT
   ═══════════════════════════════════════════ */
const ContactPage = () => {
  useScrollAnimation();
  const [formData, setFormData] = useState({ name:'', email:'', subject:'', message:'' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <div style={{ paddingTop:100 }}>
      <section style={{ padding:'80px 5%' }}>
        <SectionTitle tag="Contact" title="Get in Touch"
          subtitle="Have questions about COREX Network, CRX-20, or BRX? We're here to help." />

        <div style={{ maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:40 }}>
          {/* Contact Info */}
          <div>
            <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
              {[
                { icon:'📧', title:'Email', info:'hello@corexnetwork.org', sub:'General inquiries' },
                { icon:'💼', title:'Business', info:'partners@corexnetwork.org', sub:'Partnership opportunities' },
                { icon:'🛠️', title:'Developer Support', info:'dev@corexnetwork.org', sub:'Technical questions' },
                { icon:'🌐', title:'BRICSX.io', info:'https://bricsx.io', sub:'Token issuer website' },
              ].map((item, i) => (
                <GlowCard key={i} delay={i * 100} style={{ padding:24 }}>
                  <div style={{ display:'flex', gap:16, alignItems:'center' }}>
                    <div style={{
                      width:48, height:48, borderRadius:12,
                      background:'rgba(0,240,255,0.1)', border:'1px solid var(--border)',
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0,
                    }}>{item.icon}</div>
                    <div>
                      <div style={{ fontFamily:'Orbitron', fontSize:14, fontWeight:700, color:'#fff', marginBottom:2 }}>{item.title}</div>
                      <div style={{ fontFamily:'Space Mono', fontSize:13, color:'var(--primary)', marginBottom:2 }}>{item.info}</div>
                      <div style={{ fontFamily:'Rajdhani', fontSize:13, color:'var(--text-dim)' }}>{item.sub}</div>
                    </div>
                  </div>
                </GlowCard>
              ))}
            </div>

            {/* Social Links */}
            <div className="animate-on-scroll" style={{ marginTop:32 }}>
              <h4 style={{ fontFamily:'Orbitron', fontSize:13, color:'var(--primary)', letterSpacing:2, marginBottom:16 }}>
                FOLLOW US
              </h4>
              <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                {['Twitter / X','Telegram','Discord','GitHub','Medium'].map(s=>(
                  <span key={s} style={{
                    padding:'8px 16px', borderRadius:8, border:'1px solid var(--border)',
                    fontFamily:'Rajdhani', fontSize:14, fontWeight:600, color:'var(--text-dim)',
                    cursor:'pointer', transition:'all 0.3s',
                  }}
                  onMouseOver={e=>{e.target.style.borderColor='var(--primary)';e.target.style.color='var(--primary)';}}
                  onMouseOut={e=>{e.target.style.borderColor='var(--border)';e.target.style.color='var(--text-dim)';}}
                  >{s}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="animate-on-scroll">
            <div style={{
              background:'var(--bg-card)', border:'1px solid var(--border)',
              borderRadius:20, padding:'40px',
            }}>
              <h3 style={{ fontFamily:'Orbitron', fontSize:20, fontWeight:700, color:'#fff', marginBottom:24 }}>
                Send a Message
              </h3>
              {submitted ? (
                <div style={{
                  textAlign:'center', padding:'60px 20px',
                  animation:'scaleIn 0.5s ease',
                }}>
                  <div style={{ fontSize:48, marginBottom:16 }}>✅</div>
                  <h4 style={{ fontFamily:'Orbitron', fontSize:18, color:'var(--primary)', marginBottom:8 }}>Message Sent!</h4>
                  <p style={{ color:'var(--text-dim)' }}>We'll get back to you shortly.</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                  {[
                    { key:'name', label:'Your Name', type:'text', placeholder:'John Doe' },
                    { key:'email', label:'Email Address', type:'email', placeholder:'john@example.com' },
                    { key:'subject', label:'Subject', type:'text', placeholder:'Question about CRX-20' },
                  ].map(field => (
                    <div key={field.key}>
                      <label style={{ fontFamily:'Rajdhani', fontSize:14, fontWeight:600, color:'var(--text-dim)', display:'block', marginBottom:6 }}>
                        {field.label}
                      </label>
                      <input
                        type={field.type}
                        placeholder={field.placeholder}
                        value={formData[field.key]}
                        onChange={e => setFormData({...formData, [field.key]: e.target.value})}
                        style={{
                          width:'100%', padding:'12px 16px', borderRadius:10,
                          background:'rgba(3,7,18,0.6)', border:'1px solid var(--border)',
                          color:'var(--text)', fontFamily:'Rajdhani', fontSize:15,
                          outline:'none', transition:'border-color 0.3s',
                        }}
                        onFocus={e=>e.target.style.borderColor='var(--primary)'}
                        onBlur={e=>e.target.style.borderColor='var(--border)'}
                      />
                    </div>
                  ))}
                  <div>
                    <label style={{ fontFamily:'Rajdhani', fontSize:14, fontWeight:600, color:'var(--text-dim)', display:'block', marginBottom:6 }}>
                      Message
                    </label>
                    <textarea
                      rows={5}
                      placeholder="Tell us how we can help..."
                      value={formData.message}
                      onChange={e => setFormData({...formData, message: e.target.value})}
                      style={{
                        width:'100%', padding:'12px 16px', borderRadius:10,
                        background:'rgba(3,7,18,0.6)', border:'1px solid var(--border)',
                        color:'var(--text)', fontFamily:'Rajdhani', fontSize:15,
                        outline:'none', resize:'vertical', transition:'border-color 0.3s',
                      }}
                      onFocus={e=>e.target.style.borderColor='var(--primary)'}
                      onBlur={e=>e.target.style.borderColor='var(--border)'}
                    />
                  </div>
                  <button onClick={handleSubmit} style={{
                    padding:'14px 32px', borderRadius:10, border:'none', cursor:'pointer',
                    background:'linear-gradient(135deg, #00f0ff, #a855f7)',
                    fontFamily:'Orbitron', fontWeight:700, fontSize:14,
                    color:'#030712', letterSpacing:1, textTransform:'uppercase',
                    transition:'all 0.3s', marginTop:8,
                  }}
                  onMouseOver={e=>e.target.style.boxShadow='0 8px 40px rgba(0,240,255,0.4)'}
                  onMouseOut={e=>e.target.style.boxShadow='none'}
                  >
                    Send Message
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════
   SCROLL TO TOP ON ROUTE CHANGE
   ═══════════════════════════════════════════ */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

/* ═══════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════ */
function App() {
  return (
    <Router>
      <GlobalStyles />
      <ParticleBackground />
      <ScrollToTop />
      <div style={{ position:'relative', zIndex:1 }}>
        <Navbar />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/ecosystem" element={<EcosystemPage />} />
          <Route path="/developers" element={<DevelopersPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
